import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SCENES = [
  // Iconic openers first — a piece that stops the scroll.
  {
    kind: 'artwork',
    src: '/hero/05_banksy_flying_copper.jpg',
    artist: 'Banksy',
    title: 'Flying Copper',
    href: '/shop?artist=Banksy',
  },
  // Then a room to help the collector picture placement in situ.
  {
    kind: 'room',
    src: '/hero-rooms/01_drawing_room.jpg',
    caption: 'Placement in situ',
    subcaption: 'Hampstead drawing room · Warhol above the fireplace',
    href: '/consultations',
  },
  {
    kind: 'artwork',
    src: '/hero/03_warhol_soup.jpg',
    artist: 'Andy Warhol',
    title: "Campbell's Soup II",
    href: '/shop?artist=Andy%20Warhol',
  },
  {
    kind: 'room',
    src: '/hero-rooms/07_library.jpg',
    caption: 'From gallery to library',
    subcaption: 'Kensington residence · Michael Joseph, Terry O\u2019Neill',
    href: '/consultations',
  },
  {
    kind: 'artwork',
    src: '/hero/02_michael_joseph_beggars.jpg',
    artist: 'Michael Joseph',
    title: 'Sarum Chase · The Beggars, 1968',
    href: '/artist/michael-joseph',
  },
  {
    kind: 'room',
    src: '/hero-rooms/04_master_bedroom.jpg',
    caption: 'Lived-in curation',
    subcaption: 'Notting Hill master bedroom',
    href: '/consultations',
  },
  {
    kind: 'artwork',
    src: '/hero/04_hirst_spot.jpg',
    artist: 'Damien Hirst',
    title: 'Spot Mickey',
    href: '/shop?artist=Damien%20Hirst',
  },
  {
    kind: 'room',
    src: '/hero-rooms/06_living_room.jpg',
    caption: 'A collection you live with',
    subcaption: 'Chelsea drawing room · Hirst, Parkinson, Bown',
    href: '/consultations',
  },
  {
    kind: 'artwork',
    src: '/hero/01_jordi_aurora.jpg',
    artist: 'Jordi Diaz Alam\u00e0',
    title: 'Vulcano',
    href: '/shop?artist=Jordi%20Diaz%20Alama',
  },
];

const HeroCinematic = ({ stats }) => {
  const [index, setIndex] = useState(0);
  const [scenes, setScenes] = useState(SCENES);

  // Admin can override the cinematic scenes via Page Manager → Home →
  // hero_carousel section. Falls back to the hardcoded SCENES above so
  // there is never a moment of blank hero (also survives API downtime).
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/pages/home`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data?.sections) return;
        const carousel = data.sections.find(
          (s) => s?.type === 'hero_carousel' && Array.isArray(s?.content?.scenes),
        );
        const custom = carousel?.content?.scenes?.filter((s) => s?.src);
        if (custom && custom.length > 0) setScenes(custom);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % scenes.length), 7000);
    return () => clearInterval(t);
  }, [scenes.length]);

  const current = scenes[index] || scenes[0];
  const isRoom = current?.kind === 'room';
  const heroAlt = isRoom
    ? (current.caption || 'Collection in a private residence')
    : `${current.title} — ${current.artist}`;

  // Derive AVIF + WebP siblings for any local /hero/... or /hero-rooms/...
  // path. The image_optimiser build step drops matching .avif / .webp
  // next to every JPEG in /public, so we can point <source> at them for
  // a ~94% payload cut on modern browsers, with graceful fallback to
  // the JPEG. Skip derivation for absolute URLs and cloud-storage URLs
  // (those carry their own variants; renderer looks at `scene.variants`).
  const buildVariantSrc = (src, ext) => {
    if (!src) return null;
    if (src.startsWith('http')) return null;
    const idx = src.lastIndexOf('.');
    if (idx < 0) return null;
    return src.substring(0, idx) + ext;
  };
  const avifSrc = current?.variants?.['.avif'] || buildVariantSrc(current?.src, '.avif');
  const webpSrc = current?.variants?.['.webp'] || buildVariantSrc(current?.src, '.webp');

  return (
    <section
      className="relative w-full bg-black text-white flex flex-col sm:block sm:h-[calc(100svh-80px)] sm:min-h-[480px] sm:overflow-hidden"
      data-testid="hero-cinematic"
    >
      {/* Image zone — natural 16:9 on mobile (full room visible, no letterbox),
          absolute-fills on sm+ for edge-to-edge cinematic crop */}
      <div className="relative aspect-video w-full sm:absolute sm:inset-0 sm:aspect-auto sm:h-full overflow-hidden bg-black">
        <AnimatePresence mode="sync">
          <motion.div
            key={current.src}
            className="absolute inset-0"
            initial={{ opacity: 0, scale: 1 }}
            animate={{ opacity: 1, scale: 1.06 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 1.4 }, scale: { duration: 7, ease: 'linear' } }}
          >
            <picture>
              {avifSrc && <source srcSet={avifSrc} type="image/avif" />}
              {webpSrc && <source srcSet={webpSrc} type="image/webp" />}
              <img
                src={current.src}
                alt={heroAlt}
                className="absolute inset-0 w-full h-full object-cover object-center"
                loading="eager"
                fetchPriority="high"
              />
            </picture>
          </motion.div>
        </AnimatePresence>

        {/* Soft gradients — only applied on desktop where content overlays the image */}
        <div className="hidden sm:block absolute inset-0 bg-gradient-to-b from-black/35 via-black/10 to-black/85 pointer-events-none" />
        <div className="hidden sm:block absolute inset-0 bg-gradient-to-r from-black/55 via-black/10 to-transparent pointer-events-none" />

        {/* Top eyebrow — always inside the image zone so it reads over the room */}
        <div className="absolute top-4 left-5 sm:top-10 sm:left-10 right-5 sm:right-auto z-10">
          <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.3em] sm:tracking-[0.35em] text-white/90 sm:text-white/75 font-sans drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)]">
            Established 1976 · Hampstead, London
          </p>
        </div>
      </div>

      {/* Content zone — below image on mobile (own dark panel), overlaid bottom on sm+ */}
      <div className="relative z-10 px-5 sm:px-10 py-8 sm:py-0 flex-1 max-w-[1500px] sm:mx-auto sm:h-full sm:flex sm:flex-col sm:justify-end sm:pb-20 md:pb-28 w-full">
        {/* Mobile featured chip — room scenes use a placement caption
            instead of an artist/title so the copy always fits the imagery. */}
        <Link
          to={current.href}
          className="sm:hidden inline-flex items-center gap-2 self-start mb-4 text-[10px] uppercase tracking-[0.25em] text-white/70 hover:text-white"
          data-testid={`hero-artwork-link-mobile-${index}`}
        >
          <span>{isRoom ? 'In residence' : 'Featured'}</span>
          <span className="text-white/40">·</span>
          <span className="text-white normal-case tracking-normal text-xs font-serif italic line-clamp-1">
            {isRoom ? current.subcaption : current.title}
          </span>
        </Link>

        {/* Headline */}
        <motion.h1
          key={`h-${current.src}`}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="font-serif font-light leading-[0.95] sm:leading-[0.92] tracking-tight text-white mb-5 sm:mb-8 max-w-3xl"
          style={{ fontSize: 'clamp(2rem, 7vw, 5.5rem)' }}
        >
          Five decades championing
          <br />
          <span className="italic font-light">the art of our time.</span>
        </motion.h1>

        {/* CTA + stats row */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 sm:gap-6 max-w-4xl">
          <Link
            to="/shop"
            data-testid="hero-cta"
            className="group inline-flex items-center justify-center sm:justify-start gap-3 self-stretch sm:self-start px-6 py-3 sm:px-7 sm:py-4 bg-white text-black font-sans text-[11px] sm:text-sm uppercase tracking-[0.25em] hover:bg-white/90 transition-colors"
          >
            Browse the collection
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1.5" />
          </Link>

          {stats && (
            <div
              className="flex items-end gap-5 sm:gap-7 text-white/80"
              data-testid="homepage-stats"
            >
              <div>
                <p className="font-serif text-xl sm:text-2xl text-white leading-none" data-testid="stat-artworks">
                  {stats.artworks ? stats.artworks.toLocaleString() : '—'}
                </p>
                <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] sm:tracking-[0.25em] mt-1">Artworks</p>
              </div>
              <div className="w-px h-8 sm:h-10 bg-white/20" />
              <div>
                <p className="font-serif text-xl sm:text-2xl text-white leading-none" data-testid="stat-artists">
                  {stats.artists || '—'}
                </p>
                <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] sm:tracking-[0.25em] mt-1">Artists</p>
              </div>
              <div className="w-px h-8 sm:h-10 bg-white/20" />
              <div>
                <p className="font-serif text-xl sm:text-2xl text-white leading-none" data-testid="stat-years">
                  {stats.years || '—'}
                </p>
                <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] sm:tracking-[0.25em] mt-1">Years</p>
              </div>
            </div>
          )}
        </div>

        {/* Desktop bottom-right attribution + dots. Room scenes swap the
            artist/title pair for a placement caption + subcaption so the
            copy always mirrors the imagery. */}
        <div className="hidden sm:flex absolute bottom-8 right-10 flex-col items-end gap-3 max-w-xs text-right">
          <Link
            to={current.href}
            className="block text-white/85 hover:text-white transition-colors"
            data-testid={`hero-artwork-link-${index}`}
          >
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/60 mb-1">
              {isRoom ? 'In residence' : 'Featured'}
            </p>
            <p className="font-serif text-base sm:text-lg leading-tight">
              {isRoom ? current.caption : current.title}
            </p>
            <p className="text-xs sm:text-sm text-white/70 mt-0.5">
              {isRoom ? current.subcaption : current.artist}
            </p>
          </Link>
          <div className="flex items-center gap-2 mt-1">
            {scenes.map((scene, i) => (
              <button
                key={scene.src}
                onClick={() => setIndex(i)}
                aria-label={`Show scene ${i + 1}`}
                data-testid={`hero-dot-${i}`}
                className="group relative h-6 flex items-center"
              >
                <span
                  className={`h-[2px] rounded-full transition-all ${
                    i === index ? 'w-8 bg-white' : 'w-4 bg-white/40 group-hover:bg-white/70'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Mobile dots */}
        <div className="sm:hidden flex items-center justify-center gap-2 mt-6">
          {scenes.map((scene, i) => (
            <button
              key={scene.src}
              onClick={() => setIndex(i)}
              aria-label={`Show scene ${i + 1}`}
              data-testid={`hero-dot-mobile-${i}`}
              className="group relative w-8 h-8 flex items-center justify-center"
            >
              <span
                className={`h-[2px] rounded-full transition-all ${
                  i === index ? 'w-6 bg-white' : 'w-3 bg-white/40 group-hover:bg-white/70'
                }`}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroCinematic;
